// Phase H — мини-карта опорных станций для DetailScreen.
//
// Сворачиваемый блок (паттерн ШАПКА RINEX): свёрнут по умолчанию,
// показывает только заголовок + перечисление кодов. Тап → блок
// раскрывается и монтируется Leaflet-инстанс ~280px высотой с:
//   * Определяемая станция — крупный акцентный (parchment-orange)
//     маркер с подписью кода. Центр расследования геометрии.
//   * Опорные станции — мельче, нейтрально серые, подписи кодов
//     рядом. JetBrains Mono для consistency с другими code-surface.
//   * Линии от определяемой к каждой опорной — тонкие, полупрозрачные.
//     Показывают конфигурацию относительного решения «на глаз»:
//     LCK4 → плотная локальная индийская сеть; STAS → разнесённая
//     по Сев. Европе. Это прямой visual для будущего расследования
//     датума/тропосферы (Phase A surfaced the data; Phase H surfaces
//     the geometry).
//   * Опорные без координат (OPMT и аналоги, 0.3% в проде) — в
//     списке под картой с пометкой «нет координат», не на карте.
//     Альтернатива (плейсхолдер 0,0) хуже — это бы исказило bounds.
//
// Карта монтируется ТОЛЬКО когда блок развёрнут (через `<details>`
// open-event listener). Это критично — пустой Leaflet-инстанс в
// каждой открытой карточке съел бы память на длинной ленте.

import L, { type LatLngTuple } from 'leaflet';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, useMap } from 'react-leaflet';

import type { BaseStationRef } from '../api/types';

import './BaseStationsMap.css';

interface Props {
  /** The job's target station — code + lat/lon. Always present
   *  (job.station_code + Station.latitude/longitude_deg). If the
   *  caller can't resolve coords (sleeping station code somehow not
   *  in catalog — defensive), pass null and the block falls back to
   *  list-only rendering. */
  target: { code: string; latitude: number | null; longitude: number | null };
  /** Reference stations from the job's `base_stations` array. May
   *  include entries with null lat/lon (catalog miss — OPMT). */
  references: BaseStationRef[];
}

/** Phase H — auto-fit bounds to (target + with-coords references).
 *  react-leaflet provides no built-in fitBounds at render time; the
 *  imperative call needs the Leaflet handle, which only mounted-map
 *  children can grab via `useMap()`. Identical pattern to MapScreen's
 *  FitActiveButton (Phase 6.9 C1). */
function FitToReferences({
  points,
}: {
  points: LatLngTuple[];
}) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      // Single point — center + reasonable zoom (8 ≈ regional view,
      // enough to see surrounding labels without isolating one dot).
      map.setView(points[0]!, 8);
      return;
    }
    map.fitBounds(points, { padding: [32, 32] });
  }, [map, points]);
  return null;
}

/** Phase H — DivIcon for the target station. Large parchment-accent
 *  fill, JetBrains Mono code label inside. Reused identity styling
 *  with `.station-marker-icon` from Phase 5 keeps the visual rhythm
 *  consistent with the main map. */
function targetIcon(code: string): L.DivIcon {
  return L.divIcon({
    className: 'basemap-target-icon',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    html: `<span class="basemap-target-dot" title="${code}"></span><span class="basemap-target-label">${code}</span>`,
  });
}

/** Phase H — DivIcon for a reference station. Smaller, neutral, code
 *  label sits to the right so multiple opornye on a tight indian
 *  network don't overlap their dots. */
function referenceIcon(code: string): L.DivIcon {
  return L.divIcon({
    className: 'basemap-ref-icon',
    iconSize: [12, 12],
    iconAnchor: [6, 6],
    html: `<span class="basemap-ref-dot" title="${code}"></span><span class="basemap-ref-label">${code}</span>`,
  });
}

export function BaseStationsMap({ target, references }: Props) {
  // Phase H — `<details>` exposes the `toggle` event when the user
  // expands or collapses it. We mirror that into React state so
  // Leaflet only mounts when `open === true`. Saves ~300KB of
  // Leaflet runtime per collapsed card on the long Feed.
  const detailsRef = useRef<HTMLDetailsElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const el = detailsRef.current;
    if (!el) return;
    const onToggle = () => setOpen(el.open);
    el.addEventListener('toggle', onToggle);
    return () => el.removeEventListener('toggle', onToggle);
  }, []);

  const withCoords = useMemo(
    () =>
      references.filter(
        (r): r is BaseStationRef & { latitude: number; longitude: number } =>
          r.latitude !== null && r.longitude !== null,
      ),
    [references],
  );
  const withoutCoords = useMemo(
    () => references.filter((r) => r.latitude === null || r.longitude === null),
    [references],
  );

  // Points feeding fitBounds + the network polylines.
  const targetPoint: LatLngTuple | null =
    target.latitude !== null && target.longitude !== null
      ? [target.latitude, target.longitude]
      : null;
  const fitPoints: LatLngTuple[] = useMemo(() => {
    const pts: LatLngTuple[] = withCoords.map((r) => [
      r.latitude,
      r.longitude,
    ]);
    if (targetPoint) pts.push(targetPoint);
    return pts;
  }, [withCoords, targetPoint]);

  const codeList = references.map((r) => r.code).join(', ');

  return (
    <details
      ref={detailsRef}
      className="detail-block base-stations-block"
      data-testid="base-stations-block"
    >
      <summary className="section-head base-stations-summary">
        <span className="left">опорные станции</span>
        <span className="right base-stations-codes">{codeList}</span>
      </summary>
      {open && (
        <>
          {targetPoint && fitPoints.length > 0 ? (
            <div
              className="base-stations-map-wrap"
              data-testid="base-stations-map-wrap"
            >
              <MapContainer
                center={targetPoint}
                zoom={4}
                scrollWheelZoom={false}
                worldCopyJump
                className="base-stations-map"
              >
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://carto.com/attributions">CartoDB</a>'
                  subdomains={['a', 'b', 'c', 'd']}
                />
                <FitToReferences points={fitPoints} />
                {/* Network geometry — straight lines from the target
                    to each reference. `worldCopyJump` ensures lines
                    cross the antimeridian correctly when references
                    sit on opposite hemispheres. */}
                {withCoords.map((r) => (
                  <Polyline
                    key={`line-${r.code}`}
                    positions={[
                      targetPoint,
                      [r.latitude, r.longitude] as LatLngTuple,
                    ]}
                    pathOptions={{
                      color: 'var(--ink-3, #888)',
                      weight: 1,
                      opacity: 0.45,
                      dashArray: '4 4',
                    }}
                  />
                ))}
                <Marker
                  position={targetPoint}
                  icon={targetIcon(target.code)}
                />
                {withCoords.map((r) => (
                  <Marker
                    key={`ref-${r.code}`}
                    position={[r.latitude, r.longitude]}
                    icon={referenceIcon(r.code)}
                  />
                ))}
              </MapContainer>
            </div>
          ) : (
            <div
              className="base-stations-empty"
              data-testid="base-stations-empty"
            >
              нет координат для отрисовки сети
            </div>
          )}

          {withoutCoords.length > 0 && (
            <div
              className="base-stations-missing"
              data-testid="base-stations-missing"
            >
              <span className="base-stations-missing-label">
                нет координат:
              </span>{' '}
              {withoutCoords.map((r, i) => (
                <span key={r.code} className="base-stations-missing-code">
                  {i > 0 && ', '}
                  {r.code}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </details>
  );
}
